import { useState, useEffect, useContext, useRef } from "react";
import { MonerisContext } from "./MonerisProvider";
import Spinner from "./Spinner";
import { useConfig } from "../ConfigContext";

export default function Payment({
  caseData,
  onPaymentCancelled,
  onPaymentApproved,
  onPaymentDeclined,
  onPaymentError,
  onPayLoaded,
  adminView
}) {
  console.log("new Payment instance");
  const { config, error: configError } = useConfig();
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingError, setProcessingError] = useState(false);
  const [displayCheckout, setDisplayCheckout] = useState(!adminView);
  const [paymentChannel, setPaymentChannel] = useState(null);
  // Callbacks capture stale state in their closure so we need to keep payment email current using refs.
  const paymentChannelStateRef = useRef();
  
  paymentChannelStateRef.current = paymentChannel;

  const monerisContext = useContext(MonerisContext);

  useEffect(() => {
    if(displayCheckout){
      // if we are in internal admin mode and checkout is visible
      initializeMoneris();
      return monerisContext.cleanupTransaction;
    }else{
      // if we are in admin mode but checkout is not yet visible (payment channel not populated yet)
      setDisplayCheckout(false);
      setLoading(false);
      onPayLoaded();
    }
  }, [displayCheckout]);

  const initializeMoneris = () => {
    setLoading(true);
      monerisContext
        .loadMonerisResources()
        .then(preloadTransaction)
        .catch((error) => {
          console.error(error);
          setLoading(false);
          onPaymentError();
          setLoadingError(true);
        });
  };

  const preloadTransaction = () => {
    return fetch(`${config.apiUrl}/cases/${caseData.apt_caseid}/preload-payment`, {
      method: "POST",
      headers: {
        "Api-Key": config.apiKey,
      },
    })
      .then((response) => {
        if (!response.ok) throw Error(response.statusText);
        return response.json();
      })
      .then((responseJson) => {
        if (responseJson.apt_PaymentTicket) {
          let monerisCheckout = monerisContext.getMonerisCheckout();
          monerisCheckout.setMode(monerisContext.environment);
          monerisCheckout.setCheckoutDiv("monerisCheckout");
          monerisCheckout.setCallback("error_event", console.error);
          monerisCheckout.setCallback("page_loaded", onPayLoaded);
          monerisCheckout.setCallback("cancel_transaction", cancelTransaction);
          monerisCheckout.setCallback("payment_receipt", transactionReceipt);
          monerisCheckout.setCallback("payment_complete", transactionReceipt);
          monerisCheckout.startCheckout(responseJson.apt_PaymentTicket);
          monerisCheckout.transactionTicket = responseJson.apt_PaymentTicket;
        } else {
          console.error(responseJson);
          onPaymentCancelled();
        }
        setLoading(false);
      }).catch((err) => {
        throw err;
      });
  };

  const transactionReceipt = async (response) => {
    setProcessing(true);
    monerisContext.getMonerisCheckout().closeCheckout(" ");
    monerisContext.transactionTicket = null;
    setDisplayCheckout(false);
    const body = {
      apt_MonerisTicket: JSON.parse(response).ticket,
      apt_PaymentChannel: paymentChannelStateRef.current
      // apt_PaymentEmail: paymentEmailStateRef.current,
    };
    const transactionResponse = await fetch(`${config.apiUrl}/cases/${caseData.apt_caseid}/process-receipt`, {
      method: "POST",
      headers: {
        "Api-Key": config.apiKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    if (!transactionResponse.ok) {
      console.error(`Error processing transaction: ${transactionResponse.statusText}: ${await transactionResponse.text()}`);
      setProcessingError(true);
    }else{
      const transactionResponseJSON = await transactionResponse.json();
      translateOptions(transactionResponseJSON);
      if (transactionResponseJSON.apt_transactionresult === "Accepted") {
        // HANDLE SUCCESS
        onPaymentApproved(transactionResponseJSON);
      } else {
        // setPaymentError(true);
        onPaymentDeclined(transactionResponseJSON);
      }
    }
    
    setProcessing(false);
  };

  /**
   * Translates the global option value to the human-readable label
   * @param {*} transactionResponse payment record from Dataverse
   */
  const translateOptions = (transactionResponse) => {
    // mutate transaction response
    transactionResponse.apt_transactionresult = config.globalOptions.find(option => option.Name === 'apt_transactionresult')?.Options.find(optionValue => optionValue.Value === transactionResponse.apt_transactionresult)?.Label
  }

  const cancelTransaction = (response) => {
    let monerisCheckout = monerisContext.getMonerisCheckout();
    monerisCheckout.closeCheckout(" "); //monerisCheckout.transactionTicket);
    monerisCheckout.transactionTicket = null;
    onPaymentCancelled();
  };

  const paymentEmailChanged = (e) => {
    setPaymentEmail(e.target.value);
  };

  const paymentChannelChanged = (e) => {
    // if in admin mode, payment channel must manually be filled in by internal staff before checkout is visible
    setDisplayCheckout(e.target.value ? true : false);
    setPaymentChannel(e.target.value);
  };

  return (
    <div>
      {loading && (
        <div>
          <Spinner />
        </div>
      )}
      {loadingError && (
        <h3 style={{ color: "#d30000" }}>
          There was an error loading the payment checkout. Please contact us at 1-888-448-0000 to make a payment over the phone or visit us in person at 17150 Yonge Street, Newmarket ON, 2nd floor.
        </h3>
      )}
      {processingError && (
        <h3 style={{ color: "#d30000" }}>
          There was an error processing the payment. DO NOT retry the payment, please contact apt@york.ca or 1-888-448-0000.
        </h3>
      )}
      {processing && (
        <div>
          <Spinner />
          <span> processing transaction...</span>
        </div>
      )}
      {!loading && !processing && !loadingError && (
        <>
          {/* <div className="js-form-item form-item js-form-type-textfield form-type-textfield">
              <label className="form-item__label">
                Email for Payment Receipt
              </label>
              <input
                type="text"
                id="paymentEmailInput"
                value={paymentEmail}
                onChange={paymentEmailChanged}
                autoComplete="on"
                className="js-webform-input-mask form-text form-element form-element--type-text form-element--api-textfield"
              />
            </div> */}
          {adminView && (
            <div className="js-form-item form-item js-form-type-select form-type-select">
              <label htmlFor="paymentChannelInput" className="form-required">
                Payment Channel (for internal staff only)
              </label>
              <select
                id="paymentChannelInput"
                className="form-select form-element form-element--type-select"
                autoComplete="nosir"
                value={paymentChannel}
                onChange={paymentChannelChanged}
              >
                <option value=""></option>
                {config?.globalOptions &&
                  config.globalOptions
                    .find((option) => option.Name === "apt_paymentchannel")
                    ?.Options.map((option) => (
                      <option key={option.Value} value={option.Value}>
                        {option.Label}
                      </option>
                    ))}
              </select>
            </div>
          )}
          { displayCheckout && <p>
            To pay, please enter payment details below. Enter your
            email address to receive a receipt. The payment gateway below is
            hosted by{" "}
            <a href="https://www.moneris.com/" target="_blank">
              Moneris
            </a>
            .
          </p> }
        </>
      )}
      <div className="monerisContainer">
        {displayCheckout && <div id="monerisCheckout"></div>}
      </div>
    </div>
  );
}
